import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { AppLayout } from "@/layout/app-layout";
import { RequireAuth } from "@/auth/require-auth";
import { RequireCapability } from "@/auth/require-capability";
import { LoginPage } from "@/pages/login";
import { DashboardPage } from "@/pages/dashboard";
import { ControllersPage } from "@/pages/controllers";
import { ControllerDetailPage } from "@/pages/controller-detail";
import { MeterDetailPage } from "@/pages/meter-detail";
import { FeedersPage } from "@/pages/feeders";
import { FeederDetailPage } from "@/pages/feeder-detail";
import { CompaniesPage } from "@/pages/companies";
import { SubstationsPage } from "@/pages/substations";
import { UsersPage } from "@/pages/users";
import { NotFoundPage } from "@/pages/not-found";
import { CompliancePage } from "@/pages/compliance";
import { ReportPage } from "@/pages/reports";
import { ReportingPage } from "@/pages/reporting";
import { GridImportPage } from "@/pages/grid-import";

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<ReportRedirect to="/dashboard" />} />
        <Route
          element={<RequireCapability capability="analytics:read"><Outlet /></RequireCapability>}
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/reporting" element={<Navigate to="/reporting/exports" replace />} />
          <Route path="/reporting/exports" element={<ReportingPage workspace="exports" />} />
          <Route path="/reporting/schedules" element={<ReportingPage workspace="schedules" />} />
          <Route path="/reports" element={<ReportRedirect to="/reports/performance" />} />
          <Route path="/reports/performance" element={<ReportPage report="performance" />} />
          <Route path="/reports/reliability" element={<ReportPage report="reliability" />} />
          <Route path="/reports/daily-dar" element={<ReportPage report="daily-dar" />} />
          <Route path="/reports/help" element={<ReportRedirect to="/dashboard" />} />
          <Route path="/compliance" element={<CompliancePage />} />
        </Route>
        <Route
          element={<RequireCapability capability="controllers:read"><Outlet /></RequireCapability>}
        >
          <Route path="/diagnostics" element={<Navigate to="/controllers" replace />} />
          <Route path="/controllers" element={<ControllersPage />} />
          <Route path="/controllers/:deviceId" element={<ControllerDetailPage />} />
          <Route path="/controllers/:deviceId/meters/:meterId" element={<MeterDetailPage />} />
          <Route path="/devices" element={<Navigate to="/controllers" replace />} />
          <Route path="/devices/:deviceId" element={<Navigate to="/controllers" replace />} />
        </Route>
        <Route
          path="/feeders"
          element={<RequireCapability capability="fleet:read"><FeedersPage /></RequireCapability>}
        />
        <Route
          path="/feeders/:feederId"
          element={<RequireCapability capability="fleet:read"><FeederDetailPage /></RequireCapability>}
        />
        <Route
          path="/substations"
          element={
            <RequireCapability capability="catalog:write">
              <SubstationsPage />
            </RequireCapability>
          }
        />
        <Route
          path="/imports"
          element={<RequireCapability capability="catalog:write"><GridImportPage /></RequireCapability>}
        />
        <Route
          path="/provisioning"
          element={
            <RequireCapability capability="provisioning:write">
              <Navigate to="/controllers" replace />
            </RequireCapability>
          }
        />
        <Route
          path="/companies"
          element={
            <RequireCapability capability="catalog:write">
              <CompaniesPage />
            </RequireCapability>
          }
        />
        <Route
          path="/organizations"
          element={
            <RequireCapability capability="catalog:write">
              <CompaniesPage />
            </RequireCapability>
          }
        />
        <Route
          path="/users"
          element={
            <RequireCapability capability="users:manage">
              <UsersPage />
            </RequireCapability>
          }
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function ReportRedirect({ to }: { to: string }): JSX.Element {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}
